import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { type EnrollmentEventType } from 'types'
import { getDateNumDaysFromStartDate, getDateString, getUTCToday, snakeToSentenceCase } from 'utils'

import { type CreateEnrollmentEventParams, useCreateEnrollmentEvent } from './hooks/enrollments/useCreateEnrollmentEvent'
import { useGetEnrollmentEventOptions } from './hooks/enrollments/useGetEnrollmentEventOptions'
import { Banner } from './reusable/Banner'
import { FormCheckboxField, FormDatePickerField, FormSelectField } from './reusable/form/FormFields'
import { Button } from './ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet'

type Props = {
  employeeId: string
  isOpen?: boolean
  onClose: () => void
  onEventCreated: () => void
}

type CreateEnrollmentEventInput = Omit<CreateEnrollmentEventParams, 'employee_id' | 'enrollment_window_end_date'> & {
  enrollment_window_length_in_days: number
}

const ENROLLMENT_TYPE_LABELS: Record<EnrollmentEventType, string> = {
  ichra_invite: 'ICHRA Invite',
  open_enrollment: 'Open Enrollment',
  special_enrollment: 'Special Enrollment',
}

const CURRENT_YEAR = getUTCToday().getFullYear()

const getPlanYearOptions = () => {
  const today = getUTCToday()
  const isNovemberOrLater = today.getMonth() >= 10
  const options = [{ label: CURRENT_YEAR, value: CURRENT_YEAR }]
  if (isNovemberOrLater) {
    options.push({ label: CURRENT_YEAR + 1, value: CURRENT_YEAR + 1 })
  }
  return options
}

const CreateEnrollmentEventSheet = ({ employeeId, isOpen, onClose, onEventCreated }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<CreateEnrollmentEventInput>({
    defaultValues: {
      enrollment_type: 'ichra_invite',
      enrollment_window_start_date: getDateString(getUTCToday()),
      enrollment_window_length_in_days: 14,
      plan_year: CURRENT_YEAR
    }
  })

  const createEnrollmentEventMutation = useCreateEnrollmentEvent()
  const { data: options, error: optionsError } = useGetEnrollmentEventOptions()

  const enrollmentType = watch('enrollment_type')
  const subtypeOptions = options?.enrollment_types[enrollmentType] ?? []
  const enableSubtypeSelect = subtypeOptions.some((subtype) => subtype !== null)

  useEffect(() => {
    setValue('enrollment_subtype', subtypeOptions[0] ?? undefined)
  }, [enrollmentType])

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      reset()
      createEnrollmentEventMutation.reset()
    }
  }

  const onSubmit: SubmitHandler<CreateEnrollmentEventInput> = (data) => {
    const endDate = getDateNumDaysFromStartDate(data.enrollment_window_start_date, data.enrollment_window_length_in_days.toString())

    createEnrollmentEventMutation.mutate({
      employee_id: employeeId,
      plan_year: data.plan_year,
      enrollment_type: data.enrollment_type,
      enrollment_subtype: data.enrollment_subtype || undefined,
      enrollment_window_start_date: data.enrollment_window_start_date,
      enrollment_window_end_date: getDateString(endDate),
      prevent_invite: data.prevent_invite
    },
    {
      onSuccess: () => {
        handleOnOpenChange(false)
        onEventCreated()
        toast.success('Enrollment Event created')
      }
    })
  }

  return (
    <div>
      <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
        <SheetContent className='h-dvh overflow-y-auto' aria-describedby={undefined}>
          <SheetHeader className='pb-4'>
            <SheetTitle>Create Enrollment Event</SheetTitle>
          </SheetHeader>
          {optionsError && <Banner type='critical' titleText='Unable to get Enrollment options'>{optionsError.message}</Banner>}
          {createEnrollmentEventMutation.isError &&
            <Banner type='critical' titleText={createEnrollmentEventMutation.error?.message ?? 'Unable to create Enrollment Event'}>
              {createEnrollmentEventMutation.error?.errors?.join(', ')}
            </Banner>}
          <form onSubmit={handleSubmit(onSubmit)}>
            {options &&
              <div className='flex flex-col py-6 gap-y-4'>
                <FormSelectField<CreateEnrollmentEventInput>
                  fieldId='enrollment_type'
                  fieldLabel='Enrollment Type'
                  fieldOptions={Object.keys(options.enrollment_types).map((type) => ({
                    value: type,
                    label: ENROLLMENT_TYPE_LABELS[type as EnrollmentEventType],
                  }))}
                  register={register}
                  validationRule={{ required: 'Enrollment Type is required' }}
                  error={errors.enrollment_type}
                  disabled={createEnrollmentEventMutation.isPending}
                />
                <FormSelectField<CreateEnrollmentEventInput>
                  fieldId='enrollment_subtype'
                  fieldLabel='Enrollment Subtype'
                  fieldOptions={subtypeOptions.map((subtype) => ({
                    value: subtype ?? '',
                    label: snakeToSentenceCase(subtype ?? ''),
                  }))}
                  register={register}
                  validationRule={{ required: enrollmentType === 'special_enrollment' ? 'Enrollment Subtype is required' : '' }}
                  error={errors.enrollment_subtype}
                  disabled={!enableSubtypeSelect || createEnrollmentEventMutation.isPending}
                />
                <FormDatePickerField<CreateEnrollmentEventInput>
                  fieldId='enrollment_window_start_date'
                  fieldLabel='Start date'
                  watch={watch}
                  selectedDateConversionFn={(selectedDate) =>
                    selectedDate ? new Date(selectedDate as string) : new Date()}
                  handleSelect={(date: Date | undefined) => {
                    // Convert date to string so the timezone doesn't affect how its passed to the API.
                    if (date) {
                      setValue('enrollment_window_start_date', getDateString(date), { shouldValidate: true })
                    }
                  }}
                  disabledCallback={(date) => date < getUTCToday()}
                  error={errors.enrollment_window_start_date}
                  disabled={createEnrollmentEventMutation.isPending}
                />
                <FormSelectField<CreateEnrollmentEventInput>
                  fieldId='enrollment_window_length_in_days'
                  fieldLabel='Enrollment window length'
                  fieldOptions={[
                    { label: '2 weeks', value: '14' },
                    { label: '3 weeks', value: '21' },
                    { label: '30 days', value: '30' },
                    // Do not show 60 days for open enrollment events.
                    ...(enrollmentType === 'open_enrollment'
                      ? []
                      : [{ label: '60 days', value: '60' }]),
                  ]}
                  register={register}
                  validationRule={{ required: 'Enrollment window length is required' }}
                  error={errors.enrollment_window_length_in_days}
                  disabled={createEnrollmentEventMutation.isPending}
                />
                <FormSelectField<CreateEnrollmentEventInput>
                  fieldId='plan_year'
                  fieldLabel='Plan year'
                  fieldOptions={getPlanYearOptions()}
                  register={register}
                  validationRule={{ required: 'Plan year is required' }}
                  error={errors.plan_year}
                  disabled={getPlanYearOptions().length <= 1}
                />
                <div className='flex justify-end'>
                  <FormCheckboxField<CreateEnrollmentEventInput>
                    fieldId='prevent_invite'
                    fieldLabel='Do not send invite emails for this event'
                    register={register}
                    error={errors.prevent_invite}
                    disabled={createEnrollmentEventMutation.isPending}
                  />
                </div>
              </div>}
            <footer className='mt-4 flex justify-end'>
              <Button disabled={createEnrollmentEventMutation.isPending}>
                Create Enrollment Event
              </Button>
            </footer>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default CreateEnrollmentEventSheet
